import React from 'react';
import { Loader2 } from '../icons';
import styles from './PrimaryButton.module.css';

/**
 * Props for PrimaryButton.
 */
interface PrimaryButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  loading?: boolean;
  children: React.ReactNode;
}

/**
 * PrimaryButton — sage-green filled CTA.
 * Supports ref forwarding for focus-management (e.g. AddItemBottomSheet fabRef).
 */
const PrimaryButtonBase = React.forwardRef<HTMLButtonElement, PrimaryButtonProps>(
  function PrimaryButton({ loading = false, disabled = false, children, className, ...rest }, ref) {
    const isInactive = loading || disabled;
    // CSS-module class names are typed as `string | undefined` under
    // noUncheckedIndexedAccess; every class accessed here is defined in
    // the adjacent .module.css file, so the undefined branch never fires
    // at runtime. Fall through to '' to satisfy the type.
    const classList: string[] = [styles.button ?? ''];
    if (disabled && !loading) classList.push(styles.buttonDisabled ?? '');
    if (loading) classList.push(styles.buttonLoading ?? '');
    if (className) classList.push(className);
    return (
      <button
        {...rest}
        ref={ref}
        disabled={isInactive}
        aria-busy={loading || undefined}
        className={classList.join(' ')}
      >
        {loading && <Loader2 size={16} className={styles.spinner} aria-hidden="true" />}
        {children}
      </button>
    );
  },
);

PrimaryButtonBase.displayName = 'PrimaryButton';
export const PrimaryButton = React.memo(PrimaryButtonBase);
PrimaryButton.displayName = 'PrimaryButton';
